import React, { Ref } from 'react'
import { Icon, TextField } from '@alto-avios/alto-ui'
import { useMediaQuery } from 'usehooks-ts'
import MobileModal from '../../MobileModal/MobileModal'
import {
  AutoCompleteProps,
  AutoCompleteItemAttributes,
  AUTOCOMPLETE_FIELD_ICON_PROPS,
} from './AutoComplete.types'
import { deviceSizes } from '../../../theme/deviceSizes'
import AutoComplete from './AutoComplete'

export const AutoCompleteWithMobileModal = React.forwardRef(
  <TItem extends AutoCompleteItemAttributes>(
    props: AutoCompleteProps<AutoCompleteItemAttributes>,
    forwardedRef: React.ForwardedRef<HTMLInputElement>
  ) => {
    const {
      name,
      label,
      accessibleLabel,
      value,
      error,
      disabled,
      items,
      onSelectedItemChange,
      isRequired = true,
      endIconProps = AUTOCOMPLETE_FIELD_ICON_PROPS,
    } = props
    const [isOpen, setIsOpen] = React.useState(false)
    const isLargerThanMobile = useMediaQuery(`(min-width: ${deviceSizes.medium})`)
    const childRef: Ref<HTMLInputElement> = React.useRef<HTMLInputElement | null>(null)
    const isClosingRef = React.useRef(false)
    const shouldOpenOnNextFocusRef = React.useRef(false)
    const triggerInputId = `field-${name}-mobile-trigger`

    const handlePlaceholderActivation = () => {
      isClosingRef.current = false
      setIsOpen(true)
    }

    const handleModalClose = () => {
      isClosingRef.current = true
      setIsOpen(false)
      document.getElementById(triggerInputId)?.focus()
    }

    // When the modal opens, move focus into the real AutoComplete input inside it
    React.useEffect(() => {
      if (!isOpen) {
        return
      }
      const modal = document.getElementById(`field-${name}-mobile-modal`)
      const input = modal?.querySelector<HTMLInputElement>('input')
      input?.focus()
    }, [isOpen, name])

    return (
      <MobileModal
        heading={label}
        isModalVisible={isOpen}
        handleModalClose={handleModalClose}
        id={`field-${name}-mobile-modal`}
        placeholder={
          <>
            <div
              onPointerDownCapture={() => {
                shouldOpenOnNextFocusRef.current = true
              }}
            >
              <TextField
                id={triggerInputId}
                onFocus={() => {
                  if (!shouldOpenOnNextFocusRef.current) {
                    return
                  }
                  shouldOpenOnNextFocusRef.current = false
                  handlePlaceholderActivation()
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ' || event.key === 'Spacebar') {
                    event.preventDefault()
                    handlePlaceholderActivation()
                  }
                }}
                name={name}
                label={label}
                aria-label={accessibleLabel ?? label}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                errorMessage={error}
                isInvalid={!!error}
                isDisabled={disabled}
                value={value}
                isRequired={isRequired}
              >
                <TextField.EndIcon>
                  <Icon {...endIconProps} isDecorative />
                </TextField.EndIcon>
              </TextField>
            </div>
            {!isLargerThanMobile ? props.helperMessage : null}
          </>
        }
      >
        <AutoComplete
          {...props}
          isRequired={isRequired}
          items={items}
          ref={(forwardedRef ?? childRef) as Ref<HTMLInputElement>}
          onIsOpenChange={(state) => {
            if (isClosingRef.current) {
              return
            }
            setIsOpen(state.isOpen || false)
          }}
          onSelectedItemChange={(changes) => {
            if (onSelectedItemChange) {
              onSelectedItemChange(changes)
              handleModalClose()
            }
          }}
          helperMessage={isLargerThanMobile ? props.helperMessage : undefined}
        />
      </MobileModal>
    )
  }
)
